import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  type ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import type { ElementFormatType } from "lexical";
import { type FC, useCallback, useMemo, useState } from "react";
import { Controller, type FieldError, FormProvider, useForm, useWatch } from "react-hook-form";
import { capitalizeEachWord } from "../../../../../utils/capitalize";
import { useCachedAssets } from "../../../../assets/hooks/use-cached-assets";
import { useAccountLimitsContext } from "../../../../context/account-limit-context";
import type { EmailButtonType } from "../../../../email-design/types/email-button-type";
import type { TagGroup } from "../../../../merge-tag/components/types";
import { useMomentFormSurveys } from "../../../../survey/hooks/use-moment-form-surveys";
import { positionOptions } from "../../nodes/stylable-button-node";
import { type StylableButtonForm, styleableButtonSchema } from "../../types/styleable-button";
import { useSurveysEnabledLexical } from "../hooks/use-surveys-enabled";
import { getSurveyIdFromUrl } from "../utils/survey-url";
import { ListenStatusWrapper } from "./listen-status-wrapper";
import { StyleableButtonUrl } from "./styleable-button-url";
import type { LinkType } from "./types";

export const AddStylableButtonForm: FC<{
  onCancel: () => void;
  onConfirm: (data: StylableButtonForm) => void;
  open: boolean;
  initialState: {
    text: string;
    url: string;
    buttonType: EmailButtonType;
    fullWidth: boolean;
    format: ElementFormatType;
  };
  filterTags: (x: string) => TagGroup;
  defaultLinkType?: LinkType;
}> = ({
  onCancel,
  onConfirm,
  open,
  initialState,
  filterTags,
  defaultLinkType = { type: "link_url" },
}) => {
  const { listen_entitlement } = useAccountLimitsContext();

  const surveyjs = useSurveysEnabledLexical();

  const assetsLoader = useCachedAssets();
  const surveyLoader = useMomentFormSurveys({ allowedStatuses: ["active"] });

  const methods = useForm<StylableButtonForm>({
    defaultValues: initialState,
    resolver: yupResolver(styleableButtonSchema),
  });

  const { control, handleSubmit, setValue } = methods;
  const [text, url] = useWatch({ control, name: ["text", "url"] });

  const [manualLinkType, setManualLinkType] = useState<LinkType["type"] | null>(null);

  const linkOption = useMemo<LinkType>(() => {
    // Respect manual override
    if (manualLinkType) {
      if (manualLinkType === "link_survey") {
        return {
          type: "link_survey",
          channel: surveyjs.enabled && surveyjs.type === "channel",
          id: getSurveyIdFromUrl(url) ?? undefined,
        };
      }
      return { type: manualLinkType };
    }

    // Otherwise derive from URL (survey detection)
    const survey_id = getSurveyIdFromUrl(url);
    if (surveyjs?.enabled && survey_id) {
      return {
        type: "link_survey",
        id: survey_id,
        channel: surveyjs.type === "channel",
      };
    }

    // Default fallback
    return defaultLinkType;
  }, [manualLinkType, url, surveyjs, defaultLinkType]);

  const handleCancel = useCallback(() => {
    methods.reset(initialState, {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
    });
    setManualLinkType(null);
    onCancel();
  }, [methods, initialState, onCancel]);

  const disableSubmitButton =
    linkOption.type === "link_survey" &&
    (surveyLoader.data?.length === 0 || surveyLoader.loading) &&
    listen_entitlement !== "Disabled";

  return (
    <Dialog onClose={handleCancel} open={open}>
      <FormProvider {...methods}>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onConfirm)}>
          <DialogTitle>Add button</DialogTitle>

          <DialogContent sx={{ minWidth: "500px" }}>
            <RadioGroup
              row
              id="link-option-radio"
              value={linkOption.type}
              onChange={(e) => {
                const newType = e.target.value as LinkType["type"];
                setValue("url", "");
                setManualLinkType(newType);
              }}
              sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}
            >
              <FormControlLabel
                value="link_url"
                control={<Radio id="lexical-styled-button-dialog-link-url" />}
                label="Link to URL"
              />
              {surveyjs.enabled && (
                <FormControlLabel
                  value="link_survey"
                  control={<Radio id="lexical-styled-button-dialog-link-survey" />}
                  label="Link to Survey"
                />
              )}
              <FormControlLabel
                value="link_lib"
                control={<Radio id="lexical-styled-button-dialog-link-lib" />}
                label="Link to Content Library"
              />
            </RadioGroup>

            <ListenStatusWrapper
              listenEntitlement={listen_entitlement}
              showListenIntent={linkOption.type === "link_survey"}
              surveyLoader={surveyLoader}
              handleClickLearnMore={handleCancel}
            >
              <FormControl variant="standard" fullWidth>
                <Controller
                  name="text"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      margin="dense"
                      id="text"
                      label="Button text"
                      fullWidth
                      variant="standard"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      helperText={error?.message}
                      error={Boolean(error)}
                    />
                  )}
                />

                <Controller
                  name="url"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <StyleableButtonUrl
                      linkOption={linkOption}
                      value={field.value}
                      onChange={field.onChange}
                      error={error}
                      surveyLoader={surveyLoader}
                      assetsLoader={assetsLoader}
                      filterTags={filterTags}
                    />
                  )}
                />

                <Controller
                  name="buttonType"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <StyleableButtonSelect
                      text={text}
                      onChange={field.onChange}
                      selected={field.value}
                      error={error}
                    />
                  )}
                />

                <Controller
                  name="format"
                  control={control}
                  render={({ field: formatField }) => (
                    <Controller
                      name="fullWidth"
                      control={control}
                      render={({ field: fullWidthField }) => (
                        <FormControl variant="standard">
                          <FormLabel sx={{ mt: 2, fontSize: "0.750rem" }}>Alignment</FormLabel>
                          <RadioGroup
                            row
                            id="alignment-radio"
                            value={
                              fullWidthField.value ? "Full width" : (formatField.value ?? "left")
                            }
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (newValue === "Full width") {
                                fullWidthField.onChange(true);
                                formatField.onChange("left");
                              } else {
                                fullWidthField.onChange(false);
                                formatField.onChange(newValue);
                              }
                            }}
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {[...positionOptions, "Full width"].map((positionOption, i) => (
                              <FormControlLabel
                                key={i}
                                value={positionOption}
                                control={<Radio />}
                                label={capitalizeEachWord(positionOption)}
                                id={`lexical-styled-button-dialog-${positionOption.replace(
                                  / /g,
                                  "-",
                                )}`}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      )}
                    />
                  )}
                />
              </FormControl>
            </ListenStatusWrapper>
          </DialogContent>

          <DialogActions>
            <Button
              data-analytics-id="lexical-styled-button-dialog-cancel"
              type="button"
              onClick={handleCancel}
              color="error"
            >
              Cancel
            </Button>
            <Button
              data-analytics-id="lexical-styled-button-dialog-confirm"
              type="submit"
              color="success"
              disabled={disableSubmitButton}
            >
              Confirm
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export const StyleableButtonSelect: FC<{
  onChange: (selected: EmailButtonType) => void;
  selected?: EmailButtonType;
  error?: FieldError;
  text?: string;
}> = ({ text, selected, onChange, error }) => {
  return (
    <Grid container columnSpacing={2} alignItems="stretch" sx={{ mt: 2 }}>
      <Grid size={{ xs: 12 }}>
        <FormLabel sx={{ fontSize: "0.750rem" }}>Button style</FormLabel>
      </Grid>
      <ButtonOption
        buttonType="primary_button"
        text={text}
        selected={selected}
        onChange={onChange}
      />
      <ButtonOption
        buttonType="secondary_button"
        text={text}
        selected={selected}
        onChange={onChange}
      />
      {error && (
        <Grid size={{ xs: 12 }}>
          <Typography color="error" variant="caption">
            {error.message}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

const ButtonOption: FC<{
  buttonType: EmailButtonType;
  text?: string;
  selected?: EmailButtonType;
  onChange: (selected: EmailButtonType) => void;
}> = ({ buttonType, text, selected, onChange }) => {
  return (
    <Grid key={buttonType} size={{ xs: 6 }}>
      <Box
        sx={{
          mb: 2,
          borderWidth: selected === buttonType ? "3px" : "1px",
          borderColor: selected === buttonType ? "primary.main" : "text.secondary",
          borderStyle: "solid",
          display: "flex",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <BrandComponentButton
          buttonType={buttonType}
          text={
            text && text.length > 0
              ? text
              : buttonType === "primary_button"
                ? "Primary Button"
                : "Secondary Button"
          }
          onClick={() => onChange(buttonType)}
          sx={{ alignSelf: "center" }}
        />
      </Box>
    </Grid>
  );
};

const BrandComponentButton: FC<
  {
    buttonType: EmailButtonType;
    text: string;
    onClick?: () => void;
    fullWidth?: boolean;
    label?: string;
  } & ButtonProps
> = ({ buttonType, text, fullWidth = false, onClick, ...props }) => {
  const { sx, ...rest } = props;

  return (
    <Button
      data-analytics-id={`lexical-styled-button-dialog-${buttonType}`}
      className={buttonType}
      sx={{
        ...sx,
      }}
      onClick={onClick}
      fullWidth={fullWidth}
      {...rest}
    >
      {text}
    </Button>
  );
};
